import { FeatureType } from "src/clients/features-control";
import { QueryFormValues } from "src/featureCatalogue/Queries/Create/schema";
import { generateFeatureMetadata } from "src/featureCatalogue/Queries/Create/utils";
import { UseFeatureQueryPreview } from "src/featureCatalogue/queries";

export type ColumnType = "entity" | "feature" | "grouping_key";

export const MIN_COLUMNS_FOR_NON_GLOBAL_FEATURES = 2;
export const MIN_COLUMNS_FOR_GROUPING_KEY_WITH_ENTITY = 3;
export const MIN_COLUMNS_FOR_GROUPING_KEY_WITHOUT_ENTITY = 2;

export type PreviewData = NonNullable<
  UseFeatureQueryPreview["previewData"]
>["data"];

export const autoAssignColumnTypes = (
  previewData: PreviewData,
  existingTypes?: Record<string, ColumnType>,
  hasEntity: boolean = true,
): Record<string, ColumnType> => {
  if (!previewData?.length) return {};

  const columnNames = Object.keys(previewData[0] ?? {});
  if (columnNames.length === 0) return {};

  const columnTypes: Record<string, ColumnType> = {};

  columnNames.forEach((column, index) => {
    // Keep existing type if column already has one
    if (existingTypes?.[column]) {
      columnTypes[column] = existingTypes[column];
    } else if (hasEntity) {
      // Default: first column = entity, rest = features
      columnTypes[column] = index === 0 ? "entity" : "feature";
    } else {
      // No entity selected: all columns are features
      columnTypes[column] = "feature";
    }
  });

  if (hasEntity) {
    // Ensure at least one entity column exists
    const hasEntityColumn = Object.values(columnTypes).some(
      (type) => type === "entity",
    );

    if (!hasEntityColumn && columnNames.length > 0) {
      columnTypes[columnNames[0]] = "entity";
    }

    // Enforce only one entity: first column is always the entity, demote others
    const entityColumns = Object.keys(columnTypes).filter(
      (col) => columnTypes[col] === "entity",
    );

    if (entityColumns.length > 1) {
      for (const col of entityColumns) {
        if (col !== columnNames[0]) {
          columnTypes[col] = "feature";
        }
      }
      columnTypes[columnNames[0]] = "entity";
    }
  } else {
    // No entity: ensure no entity columns exist
    for (const col of columnNames) {
      if (columnTypes[col] === "entity") {
        columnTypes[col] = "feature";
      }
    }
  }

  return columnTypes;
};

export const getEntityColumnFromTypes = (
  columnTypes: Record<string, ColumnType>,
) => {
  const entityColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "entity")
    .map(([column]) => column);

  return entityColumns[0];
};

export const handleFeatureGenerationFromColumnTypes = (
  columnTypes: Record<string, ColumnType>,
  existingFeatures: QueryFormValues["columns"]["features"],
  windowLength: number | null,
  featureDatatype?: Record<string, FeatureType | undefined>,
) => {
  // Build features array
  const featureColumns = Object.entries(columnTypes)
    .filter(([_, type]) => type === "feature")
    .map(([column]) => column);

  const features = featureColumns.map((col) => {
    const { featureKey, featureName } = generateFeatureMetadata(
      col,
      windowLength ?? undefined,
    );

    // Find existing feature for this column to preserve user edits
    const existingFeature = existingFeatures?.find(
      (f) => f.column_name === col,
    );

    if (existingFeature) {
      // Preserve user-edited fields, only update generated fields
      return {
        ...existingFeature,
        column_name: col,
        feature_key: featureKey,
        name: featureName,
      };
    }

    // New feature - use defaults
    return {
      column_name: col,
      feature_key: featureKey,
      name: featureName,
      feature_type: featureDatatype?.[col],
      feature_default: "None",
      description: "",
    };
  });

  return features;
};
